import { EXTERNALIZATION_THRESHOLD_BYTES, ScenarioPackage, ScenarioPrivacy } from '../contract';

/**
 * Privacy / redaction controls for a scenario archive. The package may contain
 * the model, element parameters, absolute file paths and usernames; these
 * toggles strip or mask sensitive data consistently across scenario.json AND all
 * artifact files before the archive is zipped.
 *
 * Out of scope (deferred, spec §23.8): full obfuscation of all sensitive data.
 */
export interface RedactionOptions {
  /** Replace any absolute host path (drive-letter / UNC) with a placeholder. */
  excludeAbsolutePaths?: boolean;
  /** Replace the directory part of absolute paths, keeping the file name. Ignored if excludeAbsolutePaths. */
  maskLocalDirectories?: boolean;
  /** Mask the recording user's name wherever it appears. */
  maskUsername?: boolean;
  /** The username value to mask. Defaults to scenario.createdBy. */
  username?: string;
  /** Drop the bundled model copy (model/*) and clear modelPathRef. */
  disableModelSave?: boolean;
  /** Drop externalized payloads (grpc/, elements/) above the threshold and clear their refs. */
  disableBigPayloadSave?: boolean;
  /** Byte threshold for disableBigPayloadSave. Defaults to the contract threshold. */
  bigPayloadThresholdBytes?: number;
  /** Informational: the build detail level (Full/Lightweight is applied at build time). */
  detailLevel?: 'full' | 'lightweight';
}

export interface RedactionReport {
  modelExcluded: boolean;
  payloadsRemoved: string[];
  absolutePathsRedacted: boolean;
  directoriesMasked: boolean;
  usernameMasked: boolean;
}

export const PATH_REDACTION = '<redacted-path>';
export const USER_REDACTION = '<redacted-user>';

/** Absolute Windows drive paths (single/double backslash or forward slash) and UNC. */
const ABS_PATH_RE = /(?:[A-Za-z]:(?:\\{1,2}|\/)|\\{2,}|\/\/)[^\s"']*/g;

const encoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;
function byteLength(value: string): number {
  return encoder ? encoder.encode(value).length : Buffer.byteLength(value, 'utf-8');
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function maskDirectory(match: string): string {
  const segments = match.split(/[\\/]+/);
  const base = segments[segments.length - 1] || '';
  return base ? `<dir>/${base}` : '<dir>';
}

/**
 * Apply redaction to the assembled archive file map. scenario.json is re-parsed
 * for structural redactions (model / big-payload refs), then every text file —
 * scenario.json included — is run through the path/username transforms so a
 * sensitive value can never survive in one file while being stripped from another.
 */
export function redactArchiveFiles(
  files: Record<string, string | Uint8Array>,
  options: RedactionOptions,
): { files: Record<string, string | Uint8Array>; report: RedactionReport } {
  const out: Record<string, string | Uint8Array> = { ...files };
  const report: RedactionReport = {
    modelExcluded: false,
    payloadsRemoved: [],
    absolutePathsRedacted: false,
    directoriesMasked: false,
    usernameMasked: false,
  };

  const pkg = JSON.parse(out['scenario.json'] as string) as ScenarioPackage;
  const username = options.username ?? pkg.scenario?.createdBy;

  if (options.disableModelSave) {
    for (const key of Object.keys(out)) {
      if (key.startsWith('model/')) {
        delete out[key];
        report.modelExcluded = true;
      }
    }
    if (pkg.revit?.document) {
      delete pkg.revit.document.modelPathRef;
    }
  }

  if (options.disableBigPayloadSave) {
    const threshold = options.bigPayloadThresholdBytes ?? EXTERNALIZATION_THRESHOLD_BYTES;
    for (const key of Object.keys(out)) {
      if (!key.startsWith('grpc/') && !key.startsWith('elements/')) {
        continue;
      }
      const content = out[key];
      const size = typeof content === 'string' ? byteLength(content) : content.byteLength;
      if (size > threshold) {
        delete out[key];
        report.payloadsRemoved.push(key);
      }
    }
    const removed = new Set(report.payloadsRemoved);
    for (const call of pkg.grpc ?? []) {
      if (call.requestRef && removed.has(call.requestRef)) {
        delete call.requestRef;
      }
      if (call.responseRef && removed.has(call.responseRef)) {
        delete call.responseRef;
      }
    }
    for (const element of pkg.elements ?? []) {
      if (element.parametersRef && removed.has(element.parametersRef)) {
        delete element.parametersRef;
      }
    }
  }

  // Record the privacy settings that were applied so the scenario stays
  // reproducible — a consumer can see exactly what was masked or excluded.
  const privacy = buildPrivacyRecord(options);
  if (privacy) {
    pkg.privacy = privacy;
  }

  // Re-serialize scenario.json with structural redactions applied.
  out['scenario.json'] = JSON.stringify(pkg, null, 2);

  const transforms: Array<(value: string) => string> = [];
  if (options.maskUsername && username) {
    const re = new RegExp(escapeRegExp(username), 'g');
    transforms.push((value) => {
      const next = value.replace(re, USER_REDACTION);
      if (next !== value) {
        report.usernameMasked = true;
      }
      return next;
    });
  }
  if (options.excludeAbsolutePaths) {
    transforms.push((value) => {
      const next = value.replace(ABS_PATH_RE, PATH_REDACTION);
      if (next !== value) {
        report.absolutePathsRedacted = true;
      }
      return next;
    });
  } else if (options.maskLocalDirectories) {
    transforms.push((value) => {
      const next = value.replace(ABS_PATH_RE, maskDirectory);
      if (next !== value) {
        report.directoriesMasked = true;
      }
      return next;
    });
  }

  if (transforms.length > 0) {
    for (const key of Object.keys(out)) {
      const content = out[key];
      if (typeof content === 'string') {
        out[key] = transforms.reduce((acc, transform) => transform(acc), content);
      }
    }
  }

  return { files: out, report };
}

/**
 * Distil the applied {@link RedactionOptions} into the persistable
 * {@link ScenarioPrivacy} block (effective values — `excludeAbsolutePaths` wins
 * over `maskLocalDirectories`). Returns `undefined` when nothing was applied so
 * scenario.json stays clean for an un-redacted archive.
 */
export function buildPrivacyRecord(options: RedactionOptions): ScenarioPrivacy | undefined {
  const privacy: ScenarioPrivacy = {};
  if (options.excludeAbsolutePaths) {
    privacy.excludeAbsolutePaths = true;
  } else if (options.maskLocalDirectories) {
    privacy.maskLocalDirectories = true;
  }
  if (options.maskUsername) {
    privacy.maskUsername = true;
  }
  if (options.disableModelSave) {
    privacy.modelExcluded = true;
  }
  if (options.disableBigPayloadSave) {
    privacy.bigPayloadsExcluded = true;
  }
  if (options.detailLevel) {
    privacy.detailLevel = options.detailLevel;
  }
  return Object.keys(privacy).length > 0 ? privacy : undefined;
}

/** English pre-download warning. Always warns; lists which protections are active. */
export const DEFAULT_PRIVACY_WARNING =
  'This scenario archive may contain sensitive data — the model, element parameters, file paths and usernames. Review the privacy options before downloading.';

export function buildPrivacyWarning(options?: RedactionOptions): string {
  if (!options) {
    return DEFAULT_PRIVACY_WARNING;
  }
  const active: string[] = [];
  if (options.excludeAbsolutePaths) active.push('absolute paths removed');
  else if (options.maskLocalDirectories) active.push('local directories masked');
  if (options.maskUsername) active.push('username masked');
  if (options.disableModelSave) active.push('model excluded');
  if (options.disableBigPayloadSave) active.push('large payloads excluded');

  if (active.length === 0) {
    return DEFAULT_PRIVACY_WARNING;
  }
  return `${DEFAULT_PRIVACY_WARNING} Active protections: ${active.join(', ')}.`;
}
